import { POS_PERMISSIONS } from '@matix/types';
import { getClient } from '@matix/api-client';
import { useUI } from '../../store/ui';
import { useModuleOn, useTenantConfig } from '../../store/tenantConfig';
import { useTheme } from '../../store/theme';
import { useCatalogSync } from '../catalog/hooks';
import { useParkedOrdersStore } from '../parked-orders';
import { useHeldOrders } from '../../store/heldOrders';
import { useCanI } from '../../lib/permissions';
import { useLang } from '../../lib/language';
import { unseenOnlineOrders, useOnlineOrders } from '../../store/onlineOrders';
import { OrderModeSwitch } from '../order-modes/OrderModeSwitch';
import { ClockIcon, BellIcon, MoonIcon, SunIcon, DollarIcon, ShoppingCartIcon, BalanceScaleIcon } from '../../components/icons';

/**
 * Topbar — 60px tall, sits above catalog area + cart panel.
 *
 * Contains:
 *   - "New sale" title
 *   - The order-mode pills (<OrderModeSwitch />) — Walk-In / Dine-in /
 *     Delivery / Pickup / Membership, each shown only when the tenant enabled
 *     it in admin → Settings → POS Settings. This is the ONLY order-type
 *     control in the shell: the old "Walk-in / Delivery" navigation tabs that
 *     used to sit next to it were a duplicate (they only switched the
 *     workspace, never the order type, and Delivery led to a placeholder
 *     screen) and were removed.
 *   - "Open orders" — opens the Parked Orders screen with a live count of the
 *     cashier's parked orders (synced + pending).
 *   - Sync status pill (Syncing… / Online · synced)
 *   - Notifications bell (stub)
 *   - Theme toggle
 */
export function Topbar() {
  const { t, lang, toggle: toggleLang } = useLang();
  const tab = useUI((s) => s.tab);
  const setTab = useUI((s) => s.setTab);
  const setScreen = useUI((s) => s.setScreen);
  const showModal = useUI((s) => s.showModal);
  const webOrdersUnseen = useOnlineOrders((s) => unseenOnlineOrders(s).length);
  const setWebOrdersOpen = useOnlineOrders((s) => s.setPanelOpen);
  const themeMode = useTheme((s) => s.theme);
  const toggleTheme = useTheme((s) => s.toggle);

  // Catalog sync status — drives the "Syncing… / Online · synced" pill.
  // The hook is observational only; the actual fetch is started by
  // useEagerCatalogLoad() in App.tsx.
  const sync = useCatalogSync();
  const syncing = sync.isFetching && !sync.data;
  const synced = sync.isSuccess && !sync.isFetching;
  const itemCount = sync.data?.changed_items.length ?? 0;

  // Number of parked orders for this cashier — the "Open orders" button is the
  // entry point to the Parked Orders screen (see LegacyApp routing).
  const openCount =
    useParkedOrdersStore((s) => s.rows.length) + useHeldOrders((s) => s.rows.length);

  // Legacy parity: "Holded Orders" gates the parked/open-orders entry.
  const canViewHolded = useCanI(POS_PERMISSIONS.ViewHoldedOrders);

  // Stock Balance report. The legacy POS gates its balance-scale icon on
  // "Sales By Salesman Summary Report" — the name belongs to a different
  // report, but matching it means anyone who can open the legacy Balances
  // modal can open this one without re-configuring their roles.
  const inventoryOn = useModuleOn('inventory');
  const canViewStockBalance = useCanI(POS_PERMISSIONS.ViewSalesBySalesman) && inventoryOn;

  // Appointments module — show a link into the scheduling app (SSO: hand off the
  // current cashier session JWT so the user isn't asked to log in again).
  const appointmentsEnabled = useTenantConfig((s) => s.appointmentsEnabled);

  // Kitchen Display. Gated on IsRestaurant, the same tenant flag that gates
  // dine-in and tables: a retail tenant has no kitchen, and an always-present
  // button into an empty board is noise on a screen with no room for it.
  // The kitchen screens belong to the Kitchen module (setup wizard); every
  // tenant onboarded before it has every module on.
  const kitchenOn = useModuleOn('kitchen');
  const isRestaurant = useTenantConfig((s) => s.isRestaurant) && kitchenOn;
  const openAppointments = () => {
    const url = (import.meta.env as Record<string, string | undefined>).VITE_APPOINTMENTS_URL ?? 'http://localhost:5277';
    const token = getClient().getToken();
    const target = token ? `${url.replace(/\/$/, '')}/#sso=${encodeURIComponent(token)}` : url;
    window.open(target, '_blank', 'noopener');
  };

  // Same SSO shape as appointments: hand the current cashier JWT over in the
  // URL hash so the kitchen screen opens straight onto the board. The service
  // app strips it from the URL on arrival. Both apps authenticate against the
  // same cashierAuth, so no second login and no token exchange.
  const openKitchenDisplay = () => {
    const url = (import.meta.env as Record<string, string | undefined>).VITE_SERVICE_URL ?? 'http://localhost:5275';
    const token = getClient().getToken();
    const target = token ? `${url.replace(/\/$/, '')}/#sso=${encodeURIComponent(token)}` : url;
    window.open(target, '_blank', 'noopener');
  };

  return (
    <header className="topbar">
      {/* Always-visible shortcut back to the catalog (main selling view) so the
          cashier can jump straight to picking categories/items for a new order
          from anywhere in the app shell (parked orders, receipts, reports…). */}
      <button
        className="topbar-new-order"
        title={t({ en: 'New order — show categories', ar: 'طلب جديد — عرض الأصناف' })}
        onClick={() => {
          setScreen('app');
          setTab('walkin');
        }}
      >
        <ShoppingCartIcon size={16} />
        {t({ en: 'New order', ar: 'طلب جديد' })}
      </button>

      <OrderModeSwitch />

      {canViewHolded && (
        <div className="topbar-tabs">
          <button
            className={`tab ${tab === 'orders' ? 'active' : ''}`}
            onClick={() => setTab('orders')}
          >
            <ClockIcon size={16} />
            {t({ en: 'Open orders', ar: 'الطلبات المفتوحة' })}
            <span className="count">{openCount}</span>
          </button>
        </div>
      )}

      <div className="topbar-spacer" />

      <div className="topbar-actions">
        <span
          className="status-pill"
          style={
            syncing
              ? { background: 'var(--warning-light)', color: 'var(--warning)' }
              : undefined
          }
          title={
            synced
              ? t({ en: `${itemCount} items cached locally · clicks are now instant`, ar: `${itemCount} صنفاً محفوظاً محلياً · النقر فوري الآن` })
              : syncing
              ? t({ en: 'Loading the full catalog in the background…', ar: 'جارٍ تحميل الكتالوج الكامل في الخلفية…' })
              : t({ en: 'Catalog status', ar: 'حالة الكتالوج' })
          }
        >
          <span className="dot" />
          {syncing ? t({ en: 'Syncing…', ar: 'جارٍ المزامنة…' }) : synced ? t({ en: `Online · ${itemCount} items synced`, ar: `متصل · تمت مزامنة ${itemCount} صنف` }) : t({ en: 'Online', ar: 'متصل' })}
        </span>
        {appointmentsEnabled && (
          <button
            className="icon-btn"
            title={t({ en: 'Appointments — open the scheduling app', ar: 'المواعيد — فتح تطبيق الجدولة' })}
            onClick={openAppointments}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="4" width="18" height="17" rx="2" />
              <path d="M3 9h18M8 3v4M16 3v4" />
            </svg>
          </button>
        )}
        {isRestaurant && (
          <button
            className="icon-btn"
            title={t({
              en: 'Kitchen Display — open the kitchen screens',
              ar: 'شاشة المطبخ — فتح شاشات المطبخ',
            })}
            onClick={openKitchenDisplay}
          >
            {/* A cloche: the one kitchen icon that reads at 20px and is not
                confused with the cart or the receipt icons already up here. */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 17h18" />
              <path d="M4.5 17a7.5 7.5 0 0 1 15 0" />
              <path d="M12 6.5V5" />
              <circle cx="12" cy="4" r="1" />
            </svg>
          </button>
        )}
        {/* Stock Balance report — same balance-scale metaphor and the same
            permission the legacy POS gates its icon on. */}
        {canViewStockBalance && (
          <button
            className="icon-btn"
            title={t({ en: 'Stock balance — on-hand quantity per warehouse', ar: 'أرصدة المخزون — الكمية المتاحة بكل مخزن' })}
            onClick={() => showModal('stockBalance')}
          >
            <BalanceScaleIcon size={20} />
          </button>
        )}
        <button
          className="icon-btn"
          title={t({ en: 'Cash drawer movement (drop / expense / pickup)', ar: 'حركة درج النقود (إيداع / مصروف / سحب)' })}
          onClick={() => showModal('cashMovement')}
        >
          <DollarIcon size={20} />
        </button>
        {/* Web orders (WooCommerce, Ordable, …) at this branch — the count is
            what this till has not acknowledged yet; the list is the last 24 h. */}
        <button
          className={`icon-btn${webOrdersUnseen > 0 ? ' has-badge' : ''}`}
          title={t({ en: 'Web orders', ar: 'طلبات المتجر' })}
          onClick={() => setWebOrdersOpen(true)}
        >
          <BellIcon size={20} />
          {webOrdersUnseen > 0 && <span className="badge">{webOrdersUnseen}</span>}
        </button>
        <button className="theme-toggle" onClick={toggleTheme} title={t({ en: 'Toggle theme', ar: 'تبديل السمة' })}>
          {themeMode === 'light' ? <MoonIcon size={20} /> : <SunIcon size={20} />}
        </button>
        <button
          className="topbar-lang-toggle"
          onClick={toggleLang}
          title={lang === 'ar' ? 'English' : 'العربية'}
          aria-label={lang === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
        >
          {lang === 'ar' ? 'EN' : 'ع'}
        </button>
      </div>
    </header>
  );
}
